'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useEffectEvent, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { acompanharPacote, avisarPacotePorEmail, decisaoPedida, esperaConfirmada, selectEtapaGeracao, selectPacoteEmEspera } from '../store';
import { ESPERA_ANTES_DE_PERGUNTAR_MS, esperandoPacote, msAte, PRAZO_DA_DECISAO_MS, segundosRestantes } from '../utils/esperaDoPacote';
const UM_SEGUNDO_MS = 1000;

/**
 * A espera do pacote montado no servidor — qualquer formato, todos saem de lá:
 *
 * 1. acompanha o pacote do começo ao fim — um pedido por pacote, abortado quando o painel desiste;
 * 2. passados 30 s, pergunta se a pessoa quer continuar esperando, com contagem de 30 s;
 * 3. sem resposta, pede o aviso por e-mail e vai para o segundo plano; com resposta (uma vez só),
 *    espera até terminar. Se o servidor para de responder às consultas, também vai para o e-mail:
 *    o pacote segue sendo gerado lá;
 * 4. quem sai no meio — fecha o painel, troca de tela ou fecha a aba — ganha o aviso na hora. Sem
 *    isso, sair antes do fim da espera deixava a pessoa sem arquivo e sem e-mail.
 *
 * @example const espera = useEsperaDoPacote(); espera.segundosParaDecidir // 27
 */
export function useEsperaDoPacote() {
  const $ = _c(47);
  const dispatch = useAppDispatch();
  const etapa = useAppSelector(selectEtapaGeracao);
  const emEspera = useAppSelector(selectPacoteEmEspera);
  const [agora, setAgora] = useState(_temp);
  const pacoteId = emEspera?.pacoteId ?? null;
  let t0;
  if ($[0] !== emEspera || $[1] !== etapa) {
    t0 = emEspera !== null && esperandoPacote(etapa);
    $[0] = emEspera;
    $[1] = etapa;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const esperando = t0;
  const esperandoSemResposta = etapa === "gerando" && emEspera !== null ? emEspera.desde : null;
  const decisaoAte = etapa === "aguardandoDecisao" ? emEspera?.decisaoAte ?? null : null;
  let t1;
  if ($[3] !== dispatch || $[4] !== emEspera || $[5] !== esperando) {
    t1 = () => {
      if (!emEspera || !esperando) {
        return;
      }
      dispatch(avisarPacotePorEmail({
        porta: emEspera.porta,
        processoId: emEspera.processoId,
        pacoteId: emEspera.pacoteId,
        aoSair: true
      }));
    };
    $[3] = dispatch;
    $[4] = emEspera;
    $[5] = esperando;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const avisarSeEmEspera = t1;
  let t2;
  if ($[7] !== dispatch || $[8] !== emEspera) {
    t2 = () => emEspera ? dispatch(acompanharPacote({
      porta: emEspera.porta,
      processoId: emEspera.processoId,
      pacoteId: emEspera.pacoteId
    })) : null;
    $[7] = dispatch;
    $[8] = emEspera;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const acompanhar = useEffectEvent(t2);
  let t3;
  if ($[10] !== dispatch || $[11] !== emEspera) {
    t3 = () => {
      if (!emEspera) {
        return;
      }
      dispatch(avisarPacotePorEmail({
        porta: emEspera.porta,
        processoId: emEspera.processoId,
        pacoteId: emEspera.pacoteId,
        aoSair: false
      }));
    };
    $[10] = dispatch;
    $[11] = emEspera;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const irParaEmail = useEffectEvent(t3);
  let t4;
  if ($[13] !== avisarSeEmEspera) {
    t4 = () => avisarSeEmEspera();
    $[13] = avisarSeEmEspera;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const sair = useEffectEvent(t4);
  let t5;
  if ($[15] !== acompanhar || $[16] !== irParaEmail || $[17] !== pacoteId) {
    t5 = () => {
      if (!pacoteId) {
        return;
      }
      const pedido = acompanhar();
      pedido?.then(resultado => {
        if (acompanharPacote.rejected.match(resultado) && !resultado.meta.aborted && resultado.payload?.motivo === "semResposta") {
          irParaEmail();
        }
      });
      return () => pedido?.abort();
    };
    $[15] = acompanhar;
    $[16] = irParaEmail;
    $[17] = pacoteId;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== pacoteId) {
    t6 = [pacoteId];
    $[19] = pacoteId;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[21] !== dispatch || $[22] !== esperandoSemResposta) {
    t7 = () => {
      if (!esperandoSemResposta) {
        return;
      }
      const timer = setTimeout(() => dispatch(decisaoPedida({
        ate: new Date(Date.now() + PRAZO_DA_DECISAO_MS).toISOString()
      })), msAte(esperandoSemResposta, ESPERA_ANTES_DE_PERGUNTAR_MS, Date.now()));
      return () => clearTimeout(timer);
    };
    t8 = [dispatch, esperandoSemResposta];
    $[21] = dispatch;
    $[22] = esperandoSemResposta;
    $[23] = t7;
    $[24] = t8;
  } else {
    t7 = $[23];
    t8 = $[24];
  }
  useEffect(t7, t8);
  let t9;
  if ($[25] !== decisaoAte || $[26] !== irParaEmail) {
    t9 = () => {
      if (!decisaoAte) {
        return;
      }
      setAgora(Date.now());
      const relogio = setInterval(() => setAgora(Date.now()), UM_SEGUNDO_MS);
      const prazo = setTimeout(() => irParaEmail(), msAte(decisaoAte, 0, Date.now()));
      return () => {
        clearInterval(relogio);
        clearTimeout(prazo);
      };
    };
    $[25] = decisaoAte;
    $[26] = irParaEmail;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== decisaoAte) {
    t10 = [decisaoAte];
    $[28] = decisaoAte;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  useEffect(t9, t10);
  let t11;
  if ($[30] !== pacoteId || $[31] !== sair) {
    t11 = () => {
      if (!pacoteId) {
        return;
      }
      const aoEsconderPagina = () => sair();
      window.addEventListener("pagehide", aoEsconderPagina);
      return () => window.removeEventListener("pagehide", aoEsconderPagina);
    };
    $[30] = pacoteId;
    $[31] = sair;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== pacoteId) {
    t12 = [pacoteId];
    $[33] = pacoteId;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  useEffect(t11, t12);
  let t13;
  if ($[35] !== sair) {
    t13 = () => () => sair();
    $[35] = sair;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  let t14;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = [];
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  useEffect(t13, t14);
  let t15;
  if ($[38] !== agora || $[39] !== decisaoAte) {
    t15 = decisaoAte ? segundosRestantes(decisaoAte, agora) : null;
    $[38] = agora;
    $[39] = decisaoAte;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  let t16;
  if ($[41] !== dispatch) {
    t16 = () => dispatch(esperaConfirmada());
    $[41] = dispatch;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  const t17 = t16;
  let t18;
  if ($[43] !== avisarSeEmEspera || $[44] !== t15 || $[45] !== t17) {
    t18 = {
      segundosParaDecidir: t15,
      continuarEsperando: t17,
      avisarSeEmEspera
    };
    $[43] = avisarSeEmEspera;
    $[44] = t15;
    $[45] = t17;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  return t18;
}
function _temp() {
  return Date.now();
}